<script setup lang="ts">
import type { Form } from '#ui/types';
import { z } from 'zod';

import { BackendErrorCode } from '~/apiClient';
import { AUTOCOMPLETE_OFF } from '~/constants/form';

interface LoginFormProps {
    email?: string;
}

// PROPS
const { email } = defineProps<LoginFormProps>();

// EVENTS
const emit = defineEmits<{
    success: [boolean];
    notFound: [string];
    notVerified: [string];
}>();

// COMPOSABLES
const { tt } = useTypedI18n();
const { isLgOrGreater } = useResponsive();
const { state: authState, getSession } = useAuthUtils();
const { trackLoginSuccess } = useTracking();
const {
    turnstileContainerId,
    sendOtp: sendLoginOtp,
    verifyOtp: verifyLoginOtp,
    isPendingSend,
    isPendingVerify,
    isSubmitting,
    resendCooldown,
    isResendDisabled,
    startResendCooldown,
} = useOtpLoginFlow();

// DATA
const emailOnlySchema = z.object({
    email: z.email(tt('validation.msg.invalid', { field: tt('login.email') })),
});

const otpSchema = z.object({
    email: z.email(tt('validation.msg.invalid', { field: tt('login.email') })),
    otp: z
        .array(z.coerce.number(tt('validation.msg.required', { field: tt('signup.verify.otp') })))
        .length(6, tt('validation.msg.invalid', { field: tt('signup.verify.otp') })),
});

type FormSchema = z.infer<typeof otpSchema>;

const isPromptingOtp = ref(false);
const formSchema = computed(() => (isPromptingOtp.value ? otpSchema : emailOnlySchema));

const formData = reactive<FormSchema>({
    email: email ?? '',
    otp: getEmptyOtp(),
});

const form = useTemplateRef<Form<FormSchema>>('form');

// COMPUTED
const submitLabel = computed(() => tt(isPromptingOtp.value ? 'login.loginButton' : 'login.nextButton'));

// METHODS
const sendOtp = async (): Promise<void> => {
    await sendLoginOtp({
        data: { email: formData.email },
        onSuccess: () => {
            isPromptingOtp.value = true;
            formData.otp = getEmptyOtp();
            form.value?.clear();
            startResendCooldown();
        },
        onBackendError: code => {
            if (code === BackendErrorCode.AuthNoUserByEmail) {
                emit('notFound', formData.email);

                return true;
            }

            if (code === BackendErrorCode.AuthEmailNotVerified) {
                emit('notVerified', formData.email);

                return true;
            }

            if (code === BackendErrorCode.OtpResendTooSoon) startResendCooldown();

            return false;
        },
    });
};

const verifyOtp = async (): Promise<void> => {
    await verifyLoginOtp({
        data: { email: formData.email, otp: formData.otp.join('') },
        onSuccess: async ({ token }) => {
            authState.setToken(token);
            await getSession();
            emit('success', true);
            trackLoginSuccess();
        },
        onBackendError: code => {
            if ([BackendErrorCode.OTPCodeNotValid, BackendErrorCode.OtpExpiredOrNotFound].includes(code)) {
                form.value?.setErrors([{ name: 'otp', message: tt('validation.msg.invalid', { field: tt('signup.verify.otp') }) }]);
            }

            return false;
        },
    });
};

const onResend = async (): Promise<void> => {
    await sendLoginOtp({
        data: { email: formData.email },
        onSuccess: () => {
            formData.otp = getEmptyOtp();
            form.value?.clear();
            startResendCooldown();
        },
        onBackendError: code => {
            if (code === BackendErrorCode.OtpResendTooSoon) startResendCooldown();

            return false;
        },
    });
};

const onSubmit = async (): Promise<void> => {
    await (isPromptingOtp.value ? verifyOtp : sendOtp)();
};

// LIFECYCLE
onMounted(() => {
    if (email) sendOtp();
});
</script>

<template>
    <div class="flex flex-col gap-6">
        <UForm
            ref="form"
            class="flex flex-col justify-center gap-6"
            :state="formData"
            :schema="formSchema"
            :validate-on="['input', 'change']"
            @submit="onSubmit"
        >
            <p class="text-muted text-center font-medium">
                {{ isPromptingOtp ? tt('signup.verify.subtitle', { channel: tt('signup.verify.resend.email') }) : tt('login.subtitle') }}
            </p>

            <template v-if="!isPromptingOtp">
                <UFormField class="mb-2 w-full" :label="tt('login.email')" name="email" required>
                    <UInput
                        v-model.trim="formData.email"
                        autocomplete="username"
                        type="email"
                        inputmode="email"
                        :disabled="isSubmitting"
                        :autofocus="isLgOrGreater"
                    />
                </UFormField>
            </template>

            <template v-else>
                <UFormField class="mb-2 w-full" :label="tt('login.email')" name="email" required>
                    <UInput :model-value="formData.email" disabled :ui="{ base: 'disabled:bg-neutral-100' }">
                        <template #trailing>
                            <span class="text-primary cursor-pointer text-sm font-bold" @click="isPromptingOtp = false">{{
                                tt('login.change')
                            }}</span>
                        </template>
                    </UInput>
                </UFormField>

                <UFormField class="w-full" :label="tt('signup.verify.otp')" name="otp" required>
                    <UPinInput
                        v-model="formData.otp"
                        :disabled="isSubmitting"
                        :autocomplete="AUTOCOMPLETE_OFF"
                        :length="6"
                        type="number"
                        otp
                        autofocus
                    />
                </UFormField>
            </template>

            <TurnstileChallenge :container-id="turnstileContainerId" />

            <UButton block :label="submitLabel" type="submit" size="xl" :loading="isSubmitting" />

            <template v-if="isPromptingOtp">
                <UButton
                    color="neutral"
                    variant="outline"
                    :label="tt('signup.verify.resend.cta')"
                    size="xl"
                    block
                    :disabled="isResendDisabled || isPendingVerify"
                    :loading="isPendingSend"
                    @click="onResend"
                >
                    <template v-if="resendCooldown" #trailing>{{ resendCooldown }}</template>
                </UButton>
            </template>
        </UForm>
    </div>
</template>
